import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Video, Search, RefreshCw, Filter, Eye, Edit2, Trash2, Plus,
  X, AlertTriangle, CheckCircle, XCircle, Play, BarChart3,
  Award, Sprout, ExternalLink, HelpCircle, Activity, Upload, Link,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { adminService } from '../services/adminService';
import { useToast } from '../contexts/ToastContext';
import { formatCompactNumber, formatPercent } from '../utils/format';
import './Campaigns.css';

interface CampaignQuestion {
  question: string;
  options: string[];
  correct_index?: number;
  correct_answer?: string;
}

interface Campaign {
  id: string;
  name: string;
  video_url: string;
  sponsor_name: string;
  country_code?: string;
  reward_tp: number;
  is_active: boolean;
  total_views: number;
  total_completions: number;
  budget_remaining?: number;
  created_at: string;
  updated_at?: string;
  questions?: CampaignQuestion[];
}

function normalizeCampaign(raw: any): Campaign {
  return {
    id: raw.id ?? '',
    name: raw.name ?? '',
    video_url: raw.video_url ?? '',
    sponsor_name: raw.sponsor_name ?? '',
    country_code: raw.country_code ?? undefined,
    reward_tp: Number(raw.reward_tp ?? 0),
    is_active: Boolean(raw.is_active),
    total_views: Number(raw.total_views ?? 0),
    total_completions: Number(raw.total_completions ?? 0),
    budget_remaining: raw.budget_remaining != null ? Number(raw.budget_remaining) : undefined,
    created_at: raw.created_at ?? '',
    updated_at: raw.updated_at ?? undefined,
    questions: Array.isArray(raw.questions) ? raw.questions : [],
  };
}

function extractArray(response: any): any[] {
  if (Array.isArray(response)) return response;
  if (response?.campaigns && Array.isArray(response.campaigns)) return response.campaigns;
  if (response?.data && Array.isArray(response.data)) return response.data;
  if (response?.items && Array.isArray(response.items)) return response.items;
  return [];
}

type ModalMode = 'view' | 'edit' | 'create' | 'delete' | 'seed' | null;

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'campaigns' | 'analytics'>('campaigns');
  const [quizAnalytics, setQuizAnalytics] = useState<any | null>(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    sponsor_name: '',
    video_url: '',
    country_code: '',
    reward_tp: '',
  });

  const toast = useToast();

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (statusFilter === 'active') params.active_only = true;
      if (countryFilter) params.country = countryFilter;

      const response = await adminService.getCampaigns(params);
      const rawList = extractArray(response);
      setCampaigns(rawList.map(normalizeCampaign));
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to load campaigns';
      setError(msg);
      toast.error('Load Failed', msg);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, countryFilter, toast]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const loadQuizAnalytics = useCallback(async () => {
    try {
      const data = await adminService.getQuizAnalytics(analyticsDays);
      setQuizAnalytics(data);
    } catch (e) {
      console.warn('quiz analytics load failed', e);
    }
  }, [analyticsDays]);

  useEffect(() => {
    if (view === 'analytics') loadQuizAnalytics();
  }, [view, loadQuizAnalytics]);

  const filteredCampaigns = campaigns.filter((c) => {
    if (statusFilter === 'inactive' && c.is_active) return false;
    if (statusFilter === 'active' && !c.is_active) return false;

    if (search) {
      const q = search.toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        c.sponsor_name.toLowerCase().includes(q) ||
        (c.country_code?.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const stats = {
    total: campaigns.length,
    active: campaigns.filter((c) => c.is_active).length,
    totalViews: campaigns.reduce((sum, c) => sum + c.total_views, 0),
    totalCompletions: campaigns.reduce((sum, c) => sum + c.total_completions, 0),
    avgRate: campaigns.length > 0
      ? campaigns.reduce((sum, c) => {
          const rate = c.total_views > 0 ? (c.total_completions / c.total_views) * 100 : 0;
          return sum + rate;
        }, 0) / campaigns.length
      : 0,
  };

  const countries = Array.from(new Set(campaigns.map((c) => c.country_code).filter(Boolean))) as string[];

  const completionRate = (c: Campaign) =>
    c.total_views > 0 ? ((c.total_completions / c.total_views) * 100) : 0;

  const rateClass = (rate: number) => {
    if (rate >= 60) return 'rate-high';
    if (rate >= 30) return 'rate-medium';
    return 'rate-low';
  };

  const openViewModal = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setModalMode('view');
    try {
      const detail = await adminService.getCampaign(campaign.id);
      const full = normalizeCampaign(detail?.campaign ?? detail?.data ?? detail);
      setSelectedCampaign(full);
    } catch {
      // Use the data we already have
    }
  };

  const openEditModal = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setFormData({
      name: campaign.name,
      sponsor_name: campaign.sponsor_name,
      video_url: campaign.video_url,
      country_code: campaign.country_code ?? '',
      reward_tp: String(campaign.reward_tp),
    });
    setModalMode('edit');
  };

  const openCreateModal = () => {
    setSelectedCampaign(null);
    setFormData({ name: '', sponsor_name: '', video_url: '', country_code: '', reward_tp: '' });
    setModalMode('create');
  };

  const openDeleteModal = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setModalMode('delete');
  };

  const openSeedModal = () => {
    setModalMode('seed');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedCampaign(null);
    setSaving(false);
  };

  const handleToggleActive = async (campaign: Campaign) => {
    setProcessingId(campaign.id);
    try {
      if (campaign.is_active) {
        await adminService.deleteCampaign(campaign.id);
        toast.success('Campaign Deactivated', `"${campaign.name}" has been deactivated`);
      } else {
        await adminService.activateCampaign(campaign.id);
        toast.success('Campaign Activated', `"${campaign.name}" is now active`);
      }
      loadCampaigns();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to update campaign status';
      toast.error('Action Failed', msg);
    } finally {
      setProcessingId(null);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.video_url.trim() || !formData.sponsor_name.trim()) {
      toast.error('Validation Error', 'Name, video URL, and sponsor name are required');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        name: formData.name.trim(),
        sponsor_name: formData.sponsor_name.trim(),
        video_url: formData.video_url.trim(),
      };
      if (formData.country_code.trim()) payload.country_code = formData.country_code.trim();
      if (formData.reward_tp) payload.reward_tp = Number(formData.reward_tp);

      await adminService.createCampaign(payload);
      toast.success('Campaign Created', `"${formData.name}" created successfully`);
      closeModal();
      loadCampaigns();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to create campaign';
      toast.error('Create Failed', msg);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedCampaign) return;
    if (!formData.name.trim() || !formData.video_url.trim() || !formData.sponsor_name.trim()) {
      toast.error('Validation Error', 'Name, video URL, and sponsor name are required');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        name: formData.name.trim(),
        sponsor_name: formData.sponsor_name.trim(),
        video_url: formData.video_url.trim(),
        country_code: formData.country_code.trim() || null,
      };
      if (formData.reward_tp) payload.reward_tp = Number(formData.reward_tp);

      await adminService.updateCampaign(selectedCampaign.id, payload);
      toast.success('Campaign Updated', `"${formData.name}" updated successfully`);
      closeModal();
      loadCampaigns();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to update campaign';
      toast.error('Update Failed', msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCampaign) return;
    setSaving(true);
    try {
      await adminService.deleteCampaign(selectedCampaign.id);
      toast.success('Campaign Deactivated', `"${selectedCampaign.name}" has been deactivated`);
      closeModal();
      loadCampaigns();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to deactivate campaign';
      toast.error('Delete Failed', msg);
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUpload = async (file: File) => {
    setUploading(true);
    setUploadProgress(10);
    try {
      const result = await adminService.uploadMedia(file, 'videos');
      const url: string = result?.public_url || result?.url || '';
      if (!url) throw new Error('No URL returned from upload');
      setFormData((prev) => ({ ...prev, video_url: url }));
      toast.success('Video Uploaded', 'Video uploaded to R2 storage successfully');
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Could not upload video';
      toast.error('Upload Failed', msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSeed = async () => {
    setSaving(true);
    try {
      await adminService.seedCampaigns();
      toast.success('Campaigns Seeded', 'Default campaigns have been created successfully');
      closeModal();
      loadCampaigns();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to seed campaigns';
      toast.error('Seed Failed', msg);
    } finally {
      setSaving(false);
    }
  };

  const renderFormFields = () => (
    <>
      <div className="form-group">
        <label>Campaign Name *</label>
        <input
          type="text"
          className="form-input"
          placeholder="e.g. Summer Promo Video"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>
      <div className="form-group">
        <label>Sponsor Name *</label>
        <input
          type="text"
          className="form-input"
          placeholder="e.g. Coca-Cola"
          value={formData.sponsor_name}
          onChange={(e) => setFormData({ ...formData, sponsor_name: e.target.value })}
        />
      </div>
      <div className="form-group">
        <label>Video *</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="url"
            className="form-input"
            placeholder="Paste URL or upload a file →"
            value={formData.video_url}
            onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '0 14px',
              background: uploading ? '#e5e7eb' : 'linear-gradient(135deg, #410C6D, #5a1494)',
              color: uploading ? '#9ca3af' : 'white', border: 'none', borderRadius: 8,
              cursor: uploading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
              fontSize: 13, fontWeight: 600,
            }}
          >
            {uploading ? <RefreshCw size={15} className="spin" /> : <Upload size={15} />}
            {uploading ? `${uploadProgress}%` : 'Upload'}
          </button>
        </div>
        {formData.video_url && (
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280' }}>
            <Link size={12} />
            <a href={formData.video_url} target="_blank" rel="noopener noreferrer"
              style={{ color: '#410C6D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
              {formData.video_url}
            </a>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleVideoUpload(file);
            e.target.value = '';
          }}
        />
      </div>
      <div className="form-group">
        <label>Country Code <span className="optional">(optional)</span></label>
        <input
          type="text"
          className="form-input"
          placeholder="e.g. NG, US, GB"
          value={formData.country_code}
          onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })}
          maxLength={3}
        />
      </div>
      <div className="form-group">
        <label>Reward TP <span className="optional">(optional)</span></label>
        <input
          type="number"
          className="form-input"
          placeholder="e.g. 50"
          value={formData.reward_tp}
          onChange={(e) => setFormData({ ...formData, reward_tp: e.target.value })}
          min={0}
        />
      </div>
    </>
  );

  return (
    <div className="campaigns-page">
      {/* Header */}
      <div className="cp-header">
        <div className="header-content">
          <h1><Video size={28} /> Campaigns — Neural Recall</h1>
          <p className="header-subtitle">Manage video ad campaigns for the AIS / Neural Recall feature</p>
        </div>
        <div className="header-actions">
          <button onClick={loadCampaigns} className="refresh-btn" disabled={loading}>
            <RefreshCw className={loading ? 'spin' : ''} size={18} />
            Refresh
          </button>
          <button onClick={openSeedModal} className="seed-btn" disabled={loading}>
            <Sprout size={18} />
            Seed
          </button>
          <button onClick={openCreateModal} className="create-btn">
            <Plus size={18} />
            New Campaign
          </button>
        </div>
      </div>

      {/* View tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className="refresh-btn"
          style={{
            background: view === 'campaigns' ? 'linear-gradient(135deg, #410C6D, #5a1494)' : 'white',
            color: view === 'campaigns' ? 'white' : '#374151',
            border: view === 'campaigns' ? 'none' : '1px solid #e5e7eb',
          }}
          onClick={() => setView('campaigns')}
        >
          <Video size={16} /> Campaigns
        </button>
        <button
          className="refresh-btn"
          style={{
            background: view === 'analytics' ? 'linear-gradient(135deg, #410C6D, #5a1494)' : 'white',
            color: view === 'analytics' ? 'white' : '#374151',
            border: view === 'analytics' ? 'none' : '1px solid #e5e7eb',
          }}
          onClick={() => setView('analytics')}
        >
          <Activity size={16} /> Analytics
        </button>
      </div>

      {/* Stats Cards */}
      <div className="cp-stats">
        <div className="stat-card total-card">
          <div className="stat-icon"><Video size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Campaigns</span>
          </div>
        </div>
        <div className="stat-card active-card">
          <div className="stat-icon"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.active}</span>
            <span className="stat-label">Active Campaigns</span>
          </div>
        </div>
        <div className="stat-card views-card">
          <div className="stat-icon"><Eye size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalViews.toLocaleString()}</span>
            <span className="stat-label">Total Views</span>
          </div>
        </div>
        <div className="stat-card completions-card">
          <div className="stat-icon"><Award size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalCompletions.toLocaleString()}</span>
            <span className="stat-label">Total Completions</span>
          </div>
        </div>
        <div className="stat-card rate-card">
          <div className="stat-icon"><BarChart3 size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{stats.avgRate.toFixed(1)}%</span>
            <span className="stat-label">Avg Completion Rate</span>
          </div>
        </div>
      </div>

      {view === 'analytics' && (
        <div className="cp-table-container" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>
              <Activity size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Neural Recall Analytics
            </h2>
            <select
              value={analyticsDays}
              onChange={(e) => setAnalyticsDays(Number(e.target.value))}
              style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: 'white' }}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          {!quizAnalytics ? (
            <div className="loading-state"><RefreshCw className="spin" size={24} /> Loading…</div>
          ) : (
            <>
              <div className="cp-stats" style={{ marginBottom: 16 }}>
                <div className="stat-card total-card">
                  <div className="stat-icon"><Activity size={24} /></div>
                  <div className="stat-content">
                    <span className="stat-value">
                      {formatCompactNumber(quizAnalytics.total_attempts ?? quizAnalytics.attempts ?? 0)}
                    </span>
                    <span className="stat-label">Quiz attempts</span>
                  </div>
                </div>
                <div className="stat-card active-card">
                  <div className="stat-icon"><CheckCircle size={24} /></div>
                  <div className="stat-content">
                    <span className="stat-value">
                      {formatCompactNumber(quizAnalytics.correct_answers ?? quizAnalytics.correct ?? 0)}
                    </span>
                    <span className="stat-label">Correct answers</span>
                  </div>
                </div>
                <div className="stat-card rate-card">
                  <div className="stat-icon"><BarChart3 size={24} /></div>
                  <div className="stat-content">
                    <span className="stat-value">
                      {(() => {
                        const acc = Number(quizAnalytics.accuracy ?? quizAnalytics.average_accuracy ?? 0);
                        return formatPercent(acc <= 1 ? acc * 100 : acc, 1);
                      })()}
                    </span>
                    <span className="stat-label">Average accuracy</span>
                  </div>
                </div>
                <div className="stat-card views-card">
                  <div className="stat-icon"><Eye size={24} /></div>
                  <div className="stat-content">
                    <span className="stat-value">
                      {formatCompactNumber(quizAnalytics.unique_users ?? quizAnalytics.users ?? 0)}
                    </span>
                    <span className="stat-label">Unique players</span>
                  </div>
                </div>
              </div>

              {(() => {
                const byCampaign =
                  quizAnalytics.by_campaign
                  || quizAnalytics.campaigns
                  || quizAnalytics.per_campaign
                  || [];
                const arr = Array.isArray(byCampaign) ? byCampaign : Object.entries(byCampaign).map(([k, v]: any) => ({ name: k, ...v }));
                if (arr.length === 0) return null;
                return (
                  <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 16, border: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem' }}>Top campaigns by attempts</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={arr.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="attempts" name="Attempts" fill="#410C6D" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              <details style={{ background: 'white', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#475569' }}>Raw response</summary>
                <pre style={{ background: '#f8fafc', padding: 12, borderRadius: 8, overflow: 'auto', fontSize: 12, marginTop: 12 }}>
                  {JSON.stringify(quizAnalytics, null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      )}

      {view === 'campaigns' && (
      <>
      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name or sponsor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="clear-search" onClick={() => setSearch('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
            <option value="">All Countries</option>
            {countries.map((code) => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Error State */}
      {error && !loading && (
        <div className="error-state">
          <AlertTriangle size={32} />
          <h3>Failed to load campaigns</h3>
          <p>{error}</p>
          <button onClick={loadCampaigns} className="retry-btn">
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      )}

      {/* Campaigns Table */}
      <div className="cp-table-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw className="spin" size={32} />
            <span>Loading campaigns...</span>
          </div>
        ) : !error ? (
          <>
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Sponsor</th>
                  <th>Country</th>
                  <th>Reward TP</th>
                  <th>Views</th>
                  <th>Completions</th>
                  <th>Rate %</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map((campaign) => {
                  const rate = completionRate(campaign);
                  return (
                    <tr key={campaign.id}>
                      <td>
                        <div className="campaign-name-cell">
                          <span className="campaign-name">{campaign.name}</span>
                          <span className="campaign-id">{campaign.id.slice(0, 8)}...</span>
                        </div>
                      </td>
                      <td className="sponsor-cell">{campaign.sponsor_name || '—'}</td>
                      <td className="country-cell">{campaign.country_code || 'Global'}</td>
                      <td className="reward-cell">{campaign.reward_tp} TP</td>
                      <td>
                        <div className="metric-cell">
                          <span className="metric-value">{campaign.total_views.toLocaleString()}</span>
                        </div>
                      </td>
                      <td>
                        <div className="metric-cell">
                          <span className="metric-value">{campaign.total_completions.toLocaleString()}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`rate-cell ${rateClass(rate)}`}>
                          {rate.toFixed(1)}%
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${campaign.is_active ? 'active' : 'inactive'}`}>
                          {campaign.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                          {campaign.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => openViewModal(campaign)}
                            className="action-btn view"
                            title="View details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => openEditModal(campaign)}
                            className="action-btn edit"
                            title="Edit campaign"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleToggleActive(campaign)}
                            className={`action-btn ${campaign.is_active ? 'deactivate' : 'activate'}`}
                            disabled={processingId === campaign.id}
                            title={campaign.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {processingId === campaign.id ? (
                              <RefreshCw size={16} className="spin" />
                            ) : campaign.is_active ? (
                              <XCircle size={16} />
                            ) : (
                              <CheckCircle size={16} />
                            )}
                          </button>
                          <button
                            onClick={() => openDeleteModal(campaign)}
                            className="action-btn delete"
                            title="Delete campaign"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredCampaigns.length === 0 && (
              <div className="no-data">
                <Video size={48} />
                <h3>No campaigns found</h3>
                <p>Try adjusting your filters or create a new campaign</p>
              </div>
            )}
          </>
        ) : null}
      </div>
      </>
      )}

      {/* View Detail Modal */}
      {modalMode === 'view' && selectedCampaign && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Campaign Details</h3>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h4><Video size={16} /> Campaign Info</h4>
                <div className="detail-grid">
                  <div className="detail-item full-width">
                    <span className="detail-label">Name</span>
                    <span className="detail-value">{selectedCampaign.name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Sponsor</span>
                    <span className="detail-value">{selectedCampaign.sponsor_name || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status</span>
                    <span className={`status-badge ${selectedCampaign.is_active ? 'active' : 'inactive'}`}>
                      {selectedCampaign.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {selectedCampaign.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Country</span>
                    <span className="detail-value">{selectedCampaign.country_code || 'Global'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Reward TP</span>
                    <span className="detail-value highlight">{selectedCampaign.reward_tp} TP</span>
                  </div>
                  <div className="detail-item full-width">
                    <span className="detail-label">Video URL</span>
                    <span className="detail-value">{selectedCampaign.video_url || '—'}</span>
                    {selectedCampaign.video_url && (
                      <div className="video-preview">
                        <a href={selectedCampaign.video_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink size={14} /> Open Video
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Campaign ID</span>
                    <span className="detail-value" style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                      {selectedCampaign.id}
                    </span>
                  </div>
                  {selectedCampaign.budget_remaining != null && (
                    <div className="detail-item">
                      <span className="detail-label">Budget Remaining</span>
                      <span className="detail-value">{selectedCampaign.budget_remaining.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h4><BarChart3 size={16} /> Performance</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Total Views</span>
                    <span className="detail-value">{selectedCampaign.total_views.toLocaleString()}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Total Completions</span>
                    <span className="detail-value">{selectedCampaign.total_completions.toLocaleString()}</span>
                  </div>
                  <div className="detail-item full-width">
                    <span className="detail-label">Completion Rate</span>
                    <span className={`detail-value ${rateClass(completionRate(selectedCampaign))}`}>
                      {completionRate(selectedCampaign).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4><Play size={16} /> Timeline</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Created</span>
                    <span className="detail-value">
                      {selectedCampaign.created_at
                        ? format(new Date(selectedCampaign.created_at), 'PPpp')
                        : '—'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Last Updated</span>
                    <span className="detail-value">
                      {selectedCampaign.updated_at
                        ? formatDistanceToNow(new Date(selectedCampaign.updated_at), { addSuffix: true })
                        : '—'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Questions Section */}
              <div className="detail-section">
                <h4><HelpCircle size={16} /> Questions ({selectedCampaign.questions?.length ?? 0})</h4>
                {selectedCampaign.questions && selectedCampaign.questions.length > 0 ? (
                  <div className="questions-list">
                    {selectedCampaign.questions.map((q, idx) => (
                      <div className="question-item" key={idx}>
                        <div className="question-text">Q{idx + 1}: {q.question}</div>
                        {Array.isArray(q.options) && q.options.length > 0 && (
                          <div className="question-options">
                            {q.options.map((opt, oi) => (
                              <span
                                className={`question-option ${
                                  (q.correct_index != null && oi === q.correct_index) ||
                                  (q.correct_answer != null && opt === q.correct_answer)
                                    ? 'correct'
                                    : ''
                                }`}
                                key={oi}
                              >
                                {String.fromCharCode(65 + oi)}. {opt}
                                {((q.correct_index != null && oi === q.correct_index) ||
                                  (q.correct_answer != null && opt === q.correct_answer)) && (
                                  <CheckCircle size={12} />
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-questions">No questions configured for this campaign</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {modalMode === 'create' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Campaign</h3>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {renderFormFields()}
            </div>
            <div className="modal-footer">
              <button onClick={closeModal} className="cancel-btn">Cancel</button>
              <button
                onClick={handleCreate}
                className="save-btn"
                disabled={saving || !formData.name.trim() || !formData.video_url.trim() || !formData.sponsor_name.trim()}
              >
                {saving ? <RefreshCw size={18} className="spin" /> : <Plus size={18} />}
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {modalMode === 'edit' && selectedCampaign && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Campaign</h3>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {renderFormFields()}
            </div>
            <div className="modal-footer">
              <button onClick={closeModal} className="cancel-btn">Cancel</button>
              <button
                onClick={handleUpdate}
                className="save-btn"
                disabled={saving || !formData.name.trim() || !formData.video_url.trim() || !formData.sponsor_name.trim()}
              >
                {saving ? <RefreshCw size={18} className="spin" /> : <Edit2 size={18} />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {modalMode === 'delete' && selectedCampaign && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Deactivate Campaign</h3>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">
                Are you sure you want to deactivate <strong>"{selectedCampaign.name}"</strong>?
              </p>
              <div className="confirm-warning">
                <AlertTriangle size={18} />
                <span>
                  This will deactivate the campaign. Users will no longer be able to view or complete it.
                  You can reactivate it later.
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeModal} className="cancel-btn">Cancel</button>
              <button onClick={handleDelete} className="confirm-delete-btn" disabled={saving}>
                {saving ? <RefreshCw size={18} className="spin" /> : <Trash2 size={18} />}
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seed Confirmation Modal */}
      {modalMode === 'seed' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Seed Default Campaigns</h3>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="seed-info">
                This will create a set of default campaigns with pre-configured video URLs, questions,
                and reward settings. Existing campaigns will not be affected.
              </div>
              <div className="confirm-warning">
                <AlertTriangle size={18} />
                <span>This action creates new campaign records in the database.</span>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeModal} className="cancel-btn">Cancel</button>
              <button onClick={handleSeed} className="confirm-seed-btn" disabled={saving}>
                {saving ? <RefreshCw size={18} className="spin" /> : <Sprout size={18} />}
                Seed Campaigns
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
